import { Pointerfocus } from 'lego-on-react';
import * as React from 'react';
import { RefObject } from 'react';
import { Fragment, RoutesLocation } from 'redux-little-router';

import { EditEntityContainer } from '../../containers/AddEntityContainer/EditEntityContainer';
import { BannerTypesPopupContainer } from '../../containers/BannerTypesPopupContainer/BannerTypesPopupContainer';
import { CreativePreviewIFrameContainer } from '../../containers/CreativePreviewIFrameContainer/CreativePreviewIFrameContainer';
import { DraggableHelpContainer } from '../../containers/DraggableHelpContainer/DraggableHelpContainer';
import { ErrorPageBoundaryContainer } from '../../containers/ErrorPageBoundaryContainer/ErrorPageBoundaryContainer';
import { FooterContainer } from '../../containers/FooterContainer/FooterContainer';
import { HeaderContainer } from '../../containers/HeaderContainer/HeaderContainer';
import { MessagesContainer } from '../../containers/MessagesContainer/MessagesContainer';
import { NoRightsPageContainer } from '../../containers/NoRightsPageContainer/NoRightsPageContainer';
import { NotificationErrorContainer } from '../../containers/NotificationErrorContainer/NotificationErrorContainer';
import { PricePackagesClientsModalContainer } from '../../containers/PricePackagesClientsModalContainer/PricePackagesClientsModalContainer';
import { RecommendationsModalContainer } from '../../containers/RecommendationsModalContainer/RecommendationsModalContainer';
import { SidebarContainer } from '../../containers/SidebarContainer/SidebarContainer';
import { RoutesPath } from '../../routes/index';
import { Bem } from '../../service/Bem/Bem';
import { AppScrollNode, PopupScopeContext } from '../../service/Dom/Dom';
import { PageLoader } from '../../service/PageLoader/PageLoader';
import { MobileAppPageMode } from '../../typings/mobile-apps';
import { ReportParams } from '../../typings/reports';
import { WelcomeScreenParams } from '../../typings/welcome';
import { AppModal } from '../AppModal/AppModal';
import {
    LazyBannersScreenContainer,
    LazyCampaignsScreenContainer,
    LazyDealsScreenRouteContainer,
    LazyFreelancerCardEditPageContainer,
    LazyFreelancerCardWrapper,
    LazyFreelancerCustomersScreenContainerWrapper,
    LazyFreelancersListScreenContainer,
    LazyGridScreenContainer,
    LazyGroupsScreenContainer,
    LazyImportExportWizardContainer,
    LazyInternalBannersScreenContainer,
    LazyLogScreenContainer,
    LazyMarketerCardContainer,
    LazyMarketersContainer,
    LazyMinusPhraseLibraryScreenContainer,
    LazyMobileAppRouteContainer,
    LazyMobileAppsListRouteContainer,
    LazyPricePackagesEditScreenContainer,
    LazyPricePackagesScreenContainer,
    LazyProductsContainer,
    LazyRecommendationsItemsScreenContainer,
    LazyRecommendationsScreenContainer,
    LazyReportsClientScreenContainer,
    LazyReportsDomainScreenContainer,
    LazyToolsScreenContainer,
    LazyUserSettingsScreenContainer,
    LazyVideoCreatorEditPageContainer,
    LazyVideoCreatorMyListPageContainer,
    LazyVideoCreatorTemplatesListPageContainer,
    LazyWelcomeScreenContainer
} from '../LazyComponents/LazyRouteComponents';
import { NotFoundPage } from '../NotFoundPage/NotFoundPage';
import { Notice } from '../Notice/Notice';

import './App.css';

export interface AppProps {
    login: string;
    welcomePageParams?: WelcomeScreenParams;
    reportsClientParams?: ReportParams;
    reportsDomainParams?: ReportParams;
    enableRecommendations: boolean;
    rmpEnabled: boolean;
    gridAllowed: boolean;
    dealsEnabled: boolean;
    domainsReportEnabled: boolean;
    dealsReadonly: boolean;
    isHeaderAvailable: boolean;
    enableSidebar: boolean;
    minusPhraseLibraryEnabled: boolean;
    fromOldInterface: boolean;
    isCampaignUpdateAllowed: boolean;
    videoEditorEnabled: boolean;
}

export class App extends React.Component<AppProps> {
    private contentNodeRef: RefObject<HTMLDivElement> = React.createRef();
    private contentRef: RefObject<HTMLDivElement> = React.createRef();
    private modalNodeRef: RefObject<HTMLDivElement> = React.createRef();

    componentDidMount(): void {
        if (this.modalNodeRef.current) {
            AppModal.setRootNode(this.modalNodeRef.current);
        }
    }

    render() {
        const { isHeaderAvailable, enableSidebar, fromOldInterface } = this.props;
        // id для RUM метрики first meaningful paint
        const dnaRoot = 'dna-root';

        return (
            <div
                className={Bem.cls({
                    block: 'app',
                    mods: { 'from-old-interface': fromOldInterface }
                })}
                id={dnaRoot}
            >
                {isHeaderAvailable && (
                    <div className={Bem.cls('app', 'header')}>
                        <HeaderContainer enableSidebar={enableSidebar} />
                    </div>
                )}
                <NotificationErrorContainer />

                <div className={Bem.cls('app', 'wrapper')}>
                    <div className={Bem.cls('app', 'sidebar')}>
                        <SidebarContainer />
                    </div>

                    <div className={Bem.cls('app', 'content')} ref={this.contentRef}>
                        <AppScrollNode.Provider value={this.getContentRef}>
                            <div ref={this.contentNodeRef} className={Bem.cls('app', 'content-per-se')}>
                                <PopupScopeContext.Provider value={this.getContentWrapperRef}>
                                    {this.getRoutes()}
                                </PopupScopeContext.Provider>

                                <FooterContainer cls={Bem.cls('app', 'footer')} />
                            </div>
                        </AppScrollNode.Provider>
                    </div>
                </div>

                <div className={Bem.cls('app', 'modals')} ref={this.modalNodeRef} />

                <Notice />

                <EditEntityContainer />

                <DraggableHelpContainer />

                <CreativePreviewIFrameContainer />

                <BannerTypesPopupContainer />

                <Pointerfocus />

                <RecommendationsModalContainer />

                <PricePackagesClientsModalContainer />

                <MessagesContainer />
            </div>
        );
    }

    private getRoutes() {
        const {
            login,
            reportsClientParams,
            reportsDomainParams,
            welcomePageParams,
            enableRecommendations,
            rmpEnabled,
            gridAllowed,
            dealsEnabled,
            domainsReportEnabled,
            dealsReadonly,
            enableSidebar,
            minusPhraseLibraryEnabled,
            videoEditorEnabled
        } = this.props;

        return (
            <Fragment forRoute="/dna">
                <ErrorPageBoundaryContainer globalScreenCls={Bem.cls('app', 'global-screen-error-message')}>
                    <React.Suspense fallback={<PageLoader />}>
                        <Fragment forRoute="/log">
                            <LazyLogScreenContainer />
                        </Fragment>
                        <Fragment forRoute="/recommendations-grid/:type">
                            <>
                                <Fragment withConditions={() => enableRecommendations}>
                                    <LazyRecommendationsItemsScreenContainer login={login} />
                                </Fragment>
                                <Fragment withConditions={() => !enableRecommendations}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/recommendations">
                            <>
                                <Fragment withConditions={() => enableRecommendations}>
                                    <LazyRecommendationsScreenContainer login={login} />
                                </Fragment>
                                <Fragment withConditions={() => !enableRecommendations}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/offline-reports">
                            <LazyReportsClientScreenContainer login={login} reportsParams={reportsClientParams} />
                        </Fragment>
                        <Fragment forRoute="/domains-reports">
                            <>
                                <Fragment withConditions={() => domainsReportEnabled}>
                                    <LazyReportsDomainScreenContainer
                                        login={login}
                                        reportsParams={reportsDomainParams}
                                    />
                                </Fragment>
                                <Fragment withConditions={() => !domainsReportEnabled}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/customers">
                            <LazyFreelancerCustomersScreenContainerWrapper />
                        </Fragment>
                        <Fragment forRoute="/welcome">
                            <LazyWelcomeScreenContainer login={login} welcomePageParams={welcomePageParams} />
                        </Fragment>
                        <Fragment forRoute="/mobile-apps">
                            <>
                                <Fragment withConditions={() => rmpEnabled} forRoute="/list">
                                    <LazyMobileAppsListRouteContainer login={login} />
                                </Fragment>
                                <Fragment withConditions={() => rmpEnabled} forRoute="/edit/:mobileAppId">
                                    <LazyMobileAppRouteContainer login={login} />
                                </Fragment>
                                <Fragment withConditions={() => rmpEnabled} forRoute="/create">
                                    <LazyMobileAppRouteContainer login={login} mode={MobileAppPageMode.create} />
                                </Fragment>
                                <Fragment withConditions={() => !rmpEnabled}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/freelancers">
                            <>
                                <Fragment forRoute="/:freelancerLogin">
                                    <>
                                        <Fragment forRoute="/edit">
                                            <LazyFreelancerCardEditPageContainer />
                                        </Fragment>
                                        <Fragment forRoute="/">
                                            <LazyFreelancerCardWrapper login={login} />
                                        </Fragment>
                                    </>
                                </Fragment>
                                <Fragment forRoute="/">
                                    <LazyFreelancersListScreenContainer />
                                </Fragment>
                                <Fragment forNoMatch={true}>
                                    <NotFoundPage />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/deals">
                            <>
                                <Fragment withConditions={() => dealsEnabled}>
                                    <LazyDealsScreenRouteContainer login={login} readonly={dealsReadonly} />
                                </Fragment>
                                <Fragment withConditions={() => !dealsEnabled}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/price-packages">
                            <>
                                <Fragment forRoute="/list">
                                    <LazyPricePackagesScreenContainer login={login} />
                                </Fragment>
                                <Fragment forRoute="/edit">
                                    <LazyPricePackagesEditScreenContainer login={login} />
                                </Fragment>
                                <Fragment forNoMatch={true}>
                                    <NotFoundPage />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/grid">
                            <>
                                <Fragment withConditions={() => gridAllowed}>
                                    <LazyGridScreenContainer enableSidebar={enableSidebar} />
                                </Fragment>
                                <Fragment withConditions={() => !gridAllowed}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>

                        <Fragment forRoute="/campaigns-edit">
                            <LazyCampaignsScreenContainer />
                        </Fragment>

                        <Fragment forRoute="/banners-edit">
                            <>
                                <Fragment withConditions={() => gridAllowed}>
                                    <LazyBannersScreenContainer />
                                </Fragment>
                                <Fragment withConditions={() => !gridAllowed}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>

                        <Fragment forRoute="/internal-banners-edit">
                            <LazyInternalBannersScreenContainer />
                        </Fragment>

                        <Fragment forRoute="/products">
                            <LazyProductsContainer />
                        </Fragment>

                        <Fragment forRoute="/marketers">
                            <>
                                <Fragment forRoute="/:id">
                                    <LazyMarketerCardContainer />
                                </Fragment>
                                <Fragment forRoute="/">
                                    <LazyMarketersContainer />
                                </Fragment>
                            </>
                        </Fragment>

                        <Fragment forRoute={'/user-settings'}>
                            <LazyUserSettingsScreenContainer />
                        </Fragment>

                        <Fragment forRoute="/groups-edit">
                            <LazyGroupsScreenContainer login={login} />
                        </Fragment>

                        <Fragment forRoute="/tools">
                            <>
                                <LazyToolsScreenContainer login={login} />
                                <Fragment forRoute="/excel">
                                    <LazyImportExportWizardContainer />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/vc">
                            <>
                                <Fragment forRoute="/list" withConditions={() => videoEditorEnabled}>
                                    <LazyVideoCreatorTemplatesListPageContainer />
                                </Fragment>
                                <Fragment forRoute="/my" withConditions={() => videoEditorEnabled}>
                                    <LazyVideoCreatorMyListPageContainer />
                                </Fragment>
                                <Fragment forRoute="/edit" withConditions={() => videoEditorEnabled}>
                                    <LazyVideoCreatorEditPageContainer />
                                </Fragment>
                                <Fragment withConditions={() => !videoEditorEnabled}>
                                    <NoRightsPageContainer />
                                </Fragment>
                                <Fragment forNoMatch={true}>
                                    <NotFoundPage />
                                </Fragment>
                            </>
                        </Fragment>
                        <Fragment forRoute="/library/minusPhrase">
                            <>
                                <Fragment withConditions={() => minusPhraseLibraryEnabled}>
                                    <LazyMinusPhraseLibraryScreenContainer />
                                </Fragment>
                                <Fragment withConditions={() => !minusPhraseLibraryEnabled}>
                                    <NoRightsPageContainer />
                                </Fragment>
                            </>
                        </Fragment>

                        <Fragment withConditions={(location: RoutesLocation) => location.route === RoutesPath.notFound}>
                            <NotFoundPage />
                        </Fragment>
                    </React.Suspense>
                </ErrorPageBoundaryContainer>
            </Fragment>
        );
    }

    private getContentWrapperRef = () => {
        return this.contentNodeRef.current ? this.contentNodeRef.current : document.body;
    };

    private getContentRef = () => {
        return this.contentRef.current ? this.contentRef.current : document.body;
    };
}
